import Link from "next/link";
import { Icon, type IconName } from "@plura/client/components";

type Props = {
    className?: string;
    icon?: IconName;
    /** Default `left`. Use `right` for trailing CTAs (e.g. arrow after label). */
    iconPosition?: "left" | "right";
    children?: React.ReactNode;
    isWhite?: boolean;
    isBlack?: boolean;
    isGray?: boolean;
    isRed?: boolean;
    isCircle?: boolean;
    isStroke?: boolean;
    processing?: boolean;
    size?: 'xs' | 'small' | 'medium' | 'large' | 'xl';
};

type ButtonProps = Props &
    (
        | (React.ButtonHTMLAttributes<HTMLButtonElement> & { as?: "button" })
        | (React.AnchorHTMLAttributes<HTMLAnchorElement> & { as: "a" })
        | ({ as: "link"; href: string } & Omit<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            "href"
        >)
        | ({ as: "span" } & React.HTMLAttributes<HTMLSpanElement>)
    );

const ProcessingDots = () => (
    <span className="absolute inset-0 flex items-center justify-center gap-1.5">
        {[0, 1, 2].map((i) => (
            <span
                key={i}
                className="h-1.5 w-1.5 rounded-full bg-current"
                style={{
                    animation: 'btn-dot-bounce 1.2s ease-in-out infinite',
                    animationDelay: `${i * 0.16}s`,
                }}
            />
        ))}
    </span>
);

const sizeClasses = {
    xs: {
        base: "h-6 rounded-xl text-button !text-[11px]",
        padding: "px-3",
        paddingVariant: "px-3",
        circle: "w-6 !px-0 rounded-full after:rounded-full",
        afterRounded: "after:rounded-xl",
    },
    small: {
        base: "h-8 rounded-2xl text-button !text-[12px]",
        padding: "px-4",
        paddingVariant: "px-4",
        circle: "w-8 !px-0 rounded-full after:rounded-full",
        afterRounded: "after:rounded-2xl",
    },
    medium: {
        base: "h-10 rounded-3xl text-button",
        padding: "px-5",
        paddingVariant: "px-5",
        circle: "w-10 !px-0 rounded-full after:rounded-full",
        afterRounded: "after:rounded-3xl",
    },
    large: {
        base: "h-12 rounded-3xl text-button",
        padding: "px-6.5",
        paddingVariant: "px-7",
        circle: "w-12 !px-0 rounded-full after:rounded-full",
        afterRounded: "after:rounded-3xl",
    },
    xl: {
        base: "h-16 rounded-4xl text-button !text-[20px]",
        padding: "px-10",
        paddingVariant: "px-8",
        circle: "w-16 !px-0 rounded-full after:rounded-full",
        afterRounded: "after:rounded-4xl",
    },
} as const;

/** Icon default is 24×24; shrink for compact buttons so glyphs match label weight. */
const iconClassForSize = (size: keyof typeof sizeClasses) =>
    size === "xs"
        ? "fill-inherit !size-3 shrink-0"
        : size === "small"
          ? "fill-inherit !size-3.5 shrink-0"
          : size === "medium"
            ? "fill-inherit !size-4 shrink-0"
            : "fill-inherit";

const Button: React.FC<ButtonProps> = ({
    className,
    icon,
    iconPosition = "left",
    children,
    isWhite,
    isBlack,
    isGray,
    isRed,
    isCircle,
    isStroke,
    processing,
    as = "button",
    size = "large",
    ...props
}) => {
    const sizeConfig = sizeClasses[size];
    const classes = `relative inline-flex items-center justify-center border transition-all cursor-pointer disabled:pointer-events-none ${sizeConfig.base} ${isWhite
        ? `bg-b-surface2 ${sizeConfig.paddingVariant} border-0 text-t-secondary fill-t-secondary hover:text-t-primary hover:fill-t-primary dark:bg-linear-to-b dark:from-[#2A2A2A] dark:to-[#202020]`
        : ""
        } ${isBlack
            ? `${sizeConfig.paddingVariant} bg-linear-to-b from-[#2C2C2C] to-[#282828] shadow-[inset_2px_0px_8px_2px_rgba(248,248,248,0.20)] border-0 text-t-light fill-t-light !transition-all after:absolute after:inset-0 after:border-[1.5px] after:border-white/20 ${sizeConfig.afterRounded} after:[mask-image:linear-gradient(to_top,transparent_0,black_100%)] hover:shadow-none dark:from-shade-10 dark:to-[#DEDEDE] dark:after:border-white/50 dark:after:shadow-[inset_2px_0px_8px_2px_rgba(24,24,24,0.25)] dark:after:transition-all dark:hover:text-shade-03 dark:hover:fill-shade-01 dark:hover:after:opacity-0`
            : ""
        } ${isGray
            ? "bg-b-surface1 border-transparent text-t-secondary fill-t-secondary hover:text-t-primary hover:fill-t-primary hover:shadow-depth dark:bg-shade-04"
            : ""
        } ${isRed
            ? "bg-primary-03/15 border-transparent text-primary-03 fill-primary-03 hover:text-t-light hover:fill-t-light hover:bg-primary-03/80 hover:shadow-depth dark:bg-shade-04"
            : ""
        } ${isCircle
            ? `${sizeConfig.circle} hover:shadow-depth`
            : sizeConfig.padding
        } ${isStroke
            ? "border-s-stroke2 fill-t-secondary text-t-secondary hover:border-s-highlight hover:text-t-primary hover:fill-t-primary disabled:opacity-50"
            : ""
        } ${processing
            ? "[animation:btn-processing-pulse_2s_ease-in-out_infinite]"
            : ""
        } ${className || ""}`;

    const content = (
        <>
            <span
                className="inline-flex items-center gap-2 transition-opacity duration-300"
                style={{ opacity: processing ? 0 : 1 }}
            >
                {icon && iconPosition === "left" && (
                    <Icon className={iconClassForSize(size)} name={icon} />
                )}
                {children}
                {icon && iconPosition === "right" && (
                    <Icon className={iconClassForSize(size)} name={icon} />
                )}
            </span>
            {processing && <ProcessingDots />}
        </>
    );

    if (as === "a") {
        return (
            <a
                className={classes}
                {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
            >
                {content}
            </a>
        );
    }

    if (as === "link") {
        return (
            <Link href={(props as { href: string }).href} className={classes}>
                {content}
            </Link>
        );
    }

    if (as === "span") {
        const spanProps = props as React.HTMLAttributes<HTMLSpanElement>;
        return (
            <span
                className={classes}
                aria-disabled={processing ? true : undefined}
                {...spanProps}
                style={{
                    ...spanProps.style,
                    ...(processing ? ({ pointerEvents: "none" } as const) : null),
                }}
            >
                {content}
            </span>
        );
    }

    const buttonProps = props as React.ButtonHTMLAttributes<HTMLButtonElement>;
    return (
        <button
            type={buttonProps.type ?? "button"}
            className={classes}
            disabled={processing || buttonProps.disabled}
            {...buttonProps}
        >
            {content}
        </button>
    );
};

export default Button;
